// Client-side password / passphrase generator (§11). Uses the browser CSPRNG with
// rejection sampling for an unbiased choice. Generation is local; nothing is sent.

const LOWER = "abcdefghijkmnpqrstuvwxyz"; // no l/o (ambiguity)
const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // no I/O
const DIGITS = "23456789"; // no 0/1
const SYMBOLS = "!@#$%^&*-_=+?";

export interface PasswordOpts {
  length: number;
  upper: boolean;
  digits: boolean;
  symbols: boolean;
}

/** Unbiased index in [0, n) from the CSPRNG (rejection sampling). */
function randIndex(n: number): number {
  const max = Math.floor(0xffffffff / n) * n;
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    if (buf[0] < max) return buf[0] % n;
  }
}

function pick(alphabet: string): string {
  return alphabet[randIndex(alphabet.length)];
}

export function generatePassword(opts: PasswordOpts): string {
  let alphabet = LOWER;
  if (opts.upper) alphabet += UPPER;
  if (opts.digits) alphabet += DIGITS;
  if (opts.symbols) alphabet += SYMBOLS;
  const len = Math.max(8, Math.min(128, opts.length));
  let out = "";
  for (let i = 0; i < len; i++) out += pick(alphabet);
  return out;
}

// Compact word list for passphrases (~7 bits/word). For high-entropy needs increase the
// word count; a full diceware list (7776 words) can be added later.
const WORDS = (
  "able acid acorn alpha amber anchor apple arbor arrow aspen atlas autumn axis bacon badge " +
  "baker basin batch beacon beam bean bear birch bison black blaze bloom blue boat bolt bonus " +
  "brave brick brisk bronze brook brush cabin cable cactus cedar chalk charm cliff cloud clover " +
  "cobalt comet copper coral cosmic crane crest crisp crown crystal dawn delta denim diamond " +
  "diner dove dragon dune eagle ember emerald falcon fern flame flint forest fox frost garden " +
  "gem ginger glacier globe gold granite grove harbor hawk hazel heron honey ivory jade jasmine " +
  "jet kayak kelp lagoon lake lantern laurel lemon lily linen lotus lunar lynx maple marble " +
  "meadow mint moon moss mountain nectar nimbus north nova oak ocean olive onyx opal orbit otter " +
  "panther pearl pebble pine pixel plum pollen poppy prairie quartz quiet raven reef ridge river " +
  "robin ruby sable sage sand sapphire shadow shore silver slate snow solar spark spruce stone " +
  "storm summit sunset tide tiger timber topaz tulip tundra valley velvet violet walnut willow " +
  "winter wolf yarrow zephyr zinc"
).split(/\s+/);

export function generatePassphrase(words: number, separator = "-"): string {
  const n = Math.max(3, Math.min(12, words));
  const parts: string[] = [];
  for (let i = 0; i < n; i++) parts.push(WORDS[randIndex(WORDS.length)]);
  return parts.join(separator);
}

export const WORDLIST_SIZE = WORDS.length;
